import { JSX, createElement } from 'react';
import parse, { domToReact, Element, DOMNode } from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';

/**
 * `isomorphic-dompurify` IS DELIBERATELY HELD AT 2.26.0 (VALE-1816). Read this
 * before taking a Dependabot bump on it — the decision was measured, not
 * inherited, and the obvious reading of the failure is wrong.
 *
 * The version that matters is NOT this one. `dompurify` — the library that
 * actually sanitises — is a transitive dependency, and 2.26.0's `^3.2.6` range
 * already resolves it to the current 3.4.14. isomorphic-dompurify v4 declares
 * `^3.4.12`, which resolves to the same 3.4.14. So the whole 2 -> 4 bump moves
 * the WRAPPER and leaves the sanitiser byte-identical: there is no security
 * delta to buy, and no advisory stands against dompurify, jsdom 26 or this
 * package.
 *
 * What the bump costs is real. The blocker is not that v4 is ESM-only (it is
 * not — v3 and v4 both ship CommonJS builds). It is `jsdom`: every
 * isomorphic-dompurify from 2.27.0 upward depends on jsdom >= 27, whose own
 * dependency tree went ESM (`@exodus/bytes`, `parse5`, `entities`,
 * `@asamuzakjp/*`, `@csstools/*`), and the frontend jest run `require`s it.
 * Because 2.27.0 — a MINOR — already crosses that line, there is no cheap
 * intermediate: v3 costs exactly what v4 costs, and so does staying on the 2.x
 * line. Two routes were measured and both were judged not worth it for a
 * zero-security-delta bump:
 *
 *   1. Adding those 10 packages to `transformIgnorePatterns` in both jest
 *      projects. Verified green, but the list IS jsdom's private dependency
 *      tree — it has no owner and drifts on any jsdom bump.
 *   2. `NODE_OPTIONS=--experimental-vm-modules` (Jest 30's native require(esm))
 *      plus web-stream polyfills in `jest.setup.js`. This changes module
 *      loading repo-wide; it got 42 failing suites down to 4 and was still not
 *      green.
 *
 * v4 additionally declares Node `^24.15.0` against our `.nvmrc` of 24.14.0, so
 * taking it means a repo-wide Node bump as well.
 *
 * TO REVISIT: the hold is on the jsdom ESM boundary, not on this package. It
 * lifts when the frontend jest run can load ESM dependencies without either
 * workaround above — a Jest/Next upgrade that makes require(esm) work
 * unflagged is the thing to watch, not a new isomorphic-dompurify release.
 * `sanitize-html-real.test.ts` is what proves the sanitiser still behaves when
 * that day comes; its mocked sibling cannot, by construction.
 */

/**
 * Sanitize an untrusted HTML string. Safe to call on both server and client —
 * `isomorphic-dompurify` uses jsdom on Node and the native DOM in the browser.
 *
 * `ADD_ATTR: ['target', 'rel']` lets `parseHtml` inject `target="_blank"`
 * + `rel="noopener noreferrer"` on anchor tags downstream without DOMPurify
 * stripping them. (LF-2981)
 */
export function sanitizeHtml(dirtyHtml: string): string {
  return DOMPurify.sanitize(dirtyHtml || '', {
    ADD_ATTR: ['target', 'rel']
  });
}

/**
 * Sanitize HTML then parse into React components.
 * Anchor tags are automatically given target="_blank" and rel="noopener noreferrer".
 *
 * Sanitization now runs on both server and client (LF-2981) — previously it
 * was skipped during SSR which let unsanitized HTML reach the first paint.
 */
export function parseHtml(
  dirtyHtml: string
): string | JSX.Element | JSX.Element[] {
  const sanitized = sanitizeHtml(dirtyHtml);

  return parse(sanitized, {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.name === 'a') {
        const props = {
          ...domNode.attribs,
          target: '_blank',
          rel: 'noopener noreferrer',
          className: 'underline'
        };
        return createElement(
          'a',
          props,
          domToReact(domNode.children as DOMNode[])
        );
      }
      return undefined;
    }
  });
}
